// BRIEF 117 §2a — THE STATUS PILL, ONCE.
//
// It was one arm of `TkStatusTag` (App.tsx), a closure over the App's task
// state that renders a `<span>` for a viewer who may not set the status and a
// `<button>` cycle toggle for one who may. §2a asks the subtask row to wear
// "the same status pill mechanism the main task carries — the row's own
// component, same vocabulary, same write path … do not build a second pill".
//
// A second pill is exactly what copying those two arms into `SubtaskChecklist`
// would have been, and it is the defect 115 closed for the Dashboard's rows
// (two renderers for one thing, every fix landing in one of them). So the
// MARKUP moves here and `TkStatusTag` keeps everything that is task-specific:
// the C82c/C94d rights test, the completion prompt, `saveTask`. None of that is
// a pill.
//
// ── WHAT IS SHARED AND WHAT IS NOT ──────────────────────────────────────────
//
// Shared: the shape (fontSize, padding, radius), the colours (whatever cfg the
// caller resolved off the firm's `task_status` taxonomy through
// `getTaskStatusMeta`), the two variants — and, since 136 §1, THE MENU.
//
// NOT shared: which codes are offered. A caller hands over `options`, because
// a task and a subtask resolve their ladder from the same taxonomy but through
// their own gates. The pill renders the list and reports the pick.
//
// ── BYTE-IDENTITY OF THE READONLY ARM ───────────────────────────────────────
//
// The two arms in App.tsx did NOT carry the same style set: the `<span>` had no
// `whiteSpace`, no `flexShrink` and no hover filter. Those stay
// interactive-only rather than being unioned onto both, because that extraction
// was a MOVE (SESSION_RULES "Extract, don't improve") and a readonly pill that
// stopped shrinking would change layout in hosts nobody re-measured.
import { useRef, useState } from "react";
import type { CSSProperties, MouseEvent } from "react";
import { createPortal } from "react-dom";
import { RAD, FS } from "../lib/tokens";
import { useAnchoredMenu } from "../hooks/useAnchoredMenu"; // 134 §1
import { useTranslation } from "../i18n";

export type StatusPillCfg = { label: string; color: string; bgColor: string };

/** One row of 136 §1's menu: the code that will be written, and the cfg the
 *  caller already resolved for it off the firm's taxonomy. */
export type StatusPillOption = { code: string; cfg: StatusPillCfg };

/* BRIEF 117 §2b — ONE WIDTH, AND IT IS WHAT MAKES THE ALIGNMENT EXACT.
   §2b asks the subtask pill to sit at its master's x. The master pill is
   LEFT-aligned inside a `width: "1px"` status column (CaseTaskRow.tsx:1247) —
   shrink-to-fit around whichever label happens to be widest in the table — so
   with ragged pills there is no x to sit at: the column is 53 wide on a table
   of To do / Ongoing and 67 on one that also holds a Completed.
   Give BOTH pills the same reservation and the problem dissolves. The status
   column is then always 65 + its own 2px padding; the subtask pill is
   right-anchored on that column's right edge (the drawer cell now ends there,
   CaseTaskRow.tsx:1463); and the two coincide on BOTH edges at any window
   width, for any row, under any status vocabulary — no measured offset in
   either file.
   MEASURED in a browser at FS.s9_5 / padding 2px 8px / 1px border:
     "To do" 41 · "Ongoing" 51 · "Completed" 65
   65 is the widest rung the shipped ladder produces. A firm whose labels run
   longer pushes past it, exactly as the subtask counter does past 99/99
   (92 §2c) — reserve for the range that occurs, not for the one that cannot. */
export const STATUS_PILL_W = 65;

/* 136 §1 — AND THE MENU IS NOT BOUND BY THAT 65.
   `useAnchoredMenu`'s contract is "as wide as the anchor", which for this
   anchor is a 65px pill: a menu that narrow would ellipsise every label it
   exists to show. `minWidth` is the parameter 134 §1 added for exactly this
   (MatterPicker's 130px host), and 150 is measured rather than picked — the
   widest shipped label, "In progress", draws 66px at `--fs-11`, and the row
   spends 10+10 on `.fs-combo-opt`'s padding, 8+8 on the swatch and its gap,
   and reserves the tick's own column. */
export const STATUS_MENU_MIN_W = 150;

/* ── WITHDRAWN 2026-08-25 — C45 AND C58, OVERRULED BY 136 §1 ────────────────
 *
 * `TK_CYCLE` and `nextStatus` stood here. Their reasoning is kept, marked
 * withdrawn rather than deleted, because it is exactly what somebody would
 * reach for when "restoring" the cycle as a fix.
 *
 *   BRIEF 118 §2 — THE CYCLE, MOVED HERE FROM `App.tsx` SO BOTH PILLS TURN THE
 *   SAME WAY. … Nothing about the order changed: Phase 29 Stage-3 C45/C58 ruled
 *   todo -> inprogress -> completed -> todo, and a status outside the cycle
 *   (cancelled / archived / the legacy "waiting") re-enters at "todo" via
 *   `indexOf` returning -1.
 *
 *     export const TK_CYCLE = ["todo", "inprogress", "completed"];
 *     export function nextStatus(code) {
 *       return TK_CYCLE[(TK_CYCLE.indexOf(String(code || "")) + 1) % 3];
 *     }
 *
 * C45 (the cycle) and C58 (dropping `waiting` from it) were deliberate. RULED
 * (Laurent, 25 Aug 2026): "the status pill when clicked should show menu with
 * all options, user to choose one, EXCEPT archived." Three of the firm's six
 * codes were unreachable from the pill — `waiting`, `cancelled` and `archived`
 * — and the first two are ordinary statuses a user must be able to set.
 *
 * THE ORDER IS NOT REPLACED BY ANOTHER ORDER. A menu has no successor
 * relation, so the ladder is simply the firm's own taxonomy order
 * (`pickableStatusCodes`, src/lib/taskCounters.ts), and the one code the
 * ruling removes is removed by name.
 *
 * C58's OTHER HALF IS WITHDRAWN TOO, one file over: `TaskModal.tsx` filtered
 * `waiting` out of its status <select> unless the row already carried it. The
 * two implemented one ruling and are withdrawn together — a pill that offers
 * `waiting` beside a modal that hides it is the disagreement C58 was written to
 * prevent, pointing the other way.
 *
 * C94a IS WITHDRAWN WITH THEM, and its premise is the argument. It swallowed a
 * pill click for 4s after a timer auto-flipped the task to `inprogress`,
 * because "the user aimed at a pill that still read 'To do' when it painted, so
 * his 'into Ongoing' click must not cycle the now-Ongoing pill into the
 * completion prompt". That hazard IS the cycle: a click whose destination is
 * computed from a status that moved under it. A menu names its destination, so
 * the same swallow would now discard a pick the user read and chose — silently.
 * ─────────────────────────────────────────────────────────────────────────── */

export default function StatusPill({
  cfg,
  current,
  title,
  options,
  onPick,
  style,
  minWidth,
}: {
  cfg: StatusPillCfg;
  /** The code this row actually holds. `cfg` carries its LABEL and colours,
   *  which a firm may make identical across two codes — so the tick is drawn
   *  from the code and never from the rendered word. */
  current?: string;
  title?: string;
  /** BRIEF 136 §1 — absent or empty = the inert display variant. Present (with
   *  `onPick`) = the live control: a click opens the menu below the pill. */
  options?: readonly StatusPillOption[];
  /** `srcDoc` is the document the pick happened in. The compact column runs in
   *  a real pop-out window and the completion prompt only ever renders in the
   *  main one (C25/C26), so the caller needs to know which it was. */
  onPick?: (code: string, srcDoc: Document) => void;
  style?: CSSProperties;
  /** A slot reservation, so two pills of different labels do not move the row
      around them (92 §2c's rule for the subtask counter, same reason). */
  minWidth?: number;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const live = !!onPick && Array.isArray(options) && options.length > 0;

  /* 134 §1's hook, not a fourth rect-in-state portal. The pill renders inside
     `tbody td` in CV/CCV, inside the Dashboard's tables and inside the subtask
     drawer — every one of them a container that clips — so the menu is
     portalled to `body` and measured against the trigger. Taking the hook also
     takes its corrections: the zoom self-calibration, the flip when there is no
     room below, and the scroll behaviour that RE-PLACES rather than closing
     (this app restores scroll programmatically through `keepScroll`, and one of
     its call sites is a task status change — the very write this menu makes).
     Hooks are called unconditionally, above the readonly branch: the inert arm
     is a variant of one component, not a second component. */
  useAnchoredMenu({
    open,
    anchorRef,
    menuRef,
    onDismiss: () => setOpen(false),
    deps: [options?.length ?? 0],
    minWidth: STATUS_MENU_MIN_W,
  });

  const base: CSSProperties = {
    fontSize: FS.s9_5,
    padding: "2px 8px",
    borderRadius: RAD.r10,
    background: cfg.bgColor,
    color: cfg.color,
    border: `1px solid ${cfg.color}50`,
    display: "inline-flex",
    alignItems: "center",
    ...(minWidth != null ? { minWidth, justifyContent: "center" } : {}),
    ...style,
  };
  if (!live)
    return (
      <span style={base} title={title}>
        {cfg.label}
      </span>
    );
  /* BRIEF 136 §1 — THE TOOLTIP, AND WHY IT COULD NOT STAY.
     It read `"<current> → <next>"` on both pills, which was an accurate
     description of a CYCLE and is a false one here: there is no next. It names
     the current status and what a click does with it. */
  const tip =
    title ??
    t("status.pill.tip", {
      label: cfg.label,
      fallback: `${cfg.label} — change status`,
    });
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        title={tip}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...base,
          cursor: "pointer",
          transition: "filter .15s",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.25)")}
        onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          /* The pill sits inside rows that navigate or expand on click. It
             stopped propagation as a cycle toggle and it still must. */
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {cfg.label}
      </button>
      {open &&
        createPortal(
          <>
            <div
              className="fs-combo-backdrop"
              onMouseDown={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            />
            <div
              ref={menuRef}
              className="fs-combo-menu"
              role="listbox"
              /* Hidden until the layout effect has measured and placed it — a
                 menu that paints at the origin for one frame is the defect
                 `useAnchoredMenu`'s header is written about. */
              style={{ visibility: "hidden", top: 0, left: 0 }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {options!.map((o) => {
                const here = current != null && o.code === current;
                return (
                  <div
                    key={o.code}
                    role="option"
                    aria-selected={here}
                    className="fs-combo-opt"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                    /* MOUSEDOWN, like `FieldSelect`'s options — 112 recorded
                       that this tree's menus commit on mousedown, and a click
                       handler here would let the backdrop's own mousedown close
                       the menu out from under the pointer first. */
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      onPick!(o.code, e.currentTarget.ownerDocument);
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: o.cfg.color,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontWeight: here ? 700 : undefined,
                      }}
                    >
                      {o.cfg.label}
                    </span>
                    {here && (
                      <span style={{ marginLeft: "auto", flexShrink: 0 }}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          </>,
          /* 136 §1 — THE PILL'S OWN DOCUMENT, not the module's `document`.
             `createPortal(…, document.body)` would open the menu in the MAIN
             window while the user is looking at the compact column's pop-out
             (App.tsx:18046 portals that column into a second document) — not
             mispositioned, invisible. `useAnchoredMenu` resolves its window the
             same way, from the same anchor. */
          (anchorRef.current?.ownerDocument ?? document).body,
        )}
    </>
  );
}
